/**
 * @fileoverview Web's display order + labels for the closed **ten**-category
 * taxonomy (WEB-606).
 *
 * The vocabulary itself is owned by `@loci/types`
 * (`CANONICAL_CATEGORY_SLUGS` / `CANONICAL_CATEGORY_LABELS`, COR-37) — this
 * module contributes only the two things the taxonomy deliberately does not
 * decide: the order chips render in, and the URL param name. Every web
 * surface that renders a category chip group, a hub page, or a filter label
 * reads from here so the ten cannot drift into a six on one surface and a ten
 * on another (the exact defect WEB-606 exists to close).
 */
import {
  CANONICAL_CATEGORY_LABELS,
  CANONICAL_CATEGORY_SLUGS,
  isCanonicalCategorySlug,
  type CanonicalCategorySlug,
} from '@loci/types';

/**
 * Display order for the canonical ten.
 *
 * Deliberately a permutation of `CANONICAL_CATEGORY_SLUGS`, not a re-declaration:
 * `satisfies` rejects any slug outside the closed set at compile time, and
 * `__tests__/categories.test.ts` proves the list is exactly ten with no
 * omissions (which the type alone cannot catch).
 *
 * Order rationale: the six pre-WEB-606 categories keep their established slot
 * order — `tech` simply renames in place to `science-tech` — so the rail's
 * visual rhythm does not shuffle for returning users, and the four net-new
 * categories append. Re-ranking by live catalog volume is a product decision,
 * not part of "make the ten render".
 */
export const CATEGORY_ORDER = [
  'music',
  'sports',
  'food',
  'science-tech',
  'art',
  'drinks',
  'business-networking',
  'community-culture',
  'wellness',
  'hobbies-interests',
] as const satisfies readonly CanonicalCategorySlug[];

export type CategorySlug = (typeof CATEGORY_ORDER)[number];

/**
 * Human labels for the ten, re-exported from the canonical taxonomy so chip
 * copy, active-filter chips, and hub hero titles cannot diverge.
 */
export const CATEGORY_LABELS: Readonly<Record<CategorySlug, string>> = CANONICAL_CATEGORY_LABELS;

/**
 * A category slug's display label, or `null` for a slug outside the ten. The
 * wire type is closed but payloads are not validated at runtime, so this checks
 * membership rather than indexing: an unmapped slug takes each surface's
 * neutral fallback instead of printing as a raw token.
 */
export function categoryLabel(slug: string): string | null {
  return isCanonicalCategorySlug(slug) ? CATEGORY_LABELS[slug] : null;
}

/** Label of an event's primary category (`categories[0]`), or `null` when none resolves. */
export function primaryCategoryLabel(categories: readonly string[] | undefined): string | null {
  const primary = categories?.[0];
  return primary === undefined ? null : categoryLabel(primary);
}

export const VALID_CATEGORIES: ReadonlySet<string> = new Set<string>(CANONICAL_CATEGORY_SLUGS);

/** Shared URL query param name for the active category filter. */
export const CATEGORY_QUERY_PARAM = 'category';

/**
 * Sort categories by the canonical LOCI · CLT order defined in
 * `CATEGORY_ORDER`. Slugs not present in the order are appended at the end
 * in their original source order (stable sort + `Infinity` sentinel).
 */
export function sortCategoriesByOrder<T extends { slug: string }>(
  categories: ReadonlyArray<T>,
): T[] {
  return [...categories].sort((a, b) => {
    const ai = CATEGORY_ORDER.indexOf(a.slug as CategorySlug);
    const bi = CATEGORY_ORDER.indexOf(b.slug as CategorySlug);
    return (ai === -1 ? Infinity : ai) - (bi === -1 ? Infinity : bi);
  });
}
